import React, { useState, useEffect, useContext } from "react";

import {
  IconButton,
  Paper,
  TableContainer,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Table,
  Stack,
  Button,
  Tooltip,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import { isEmpty } from "lodash";
import { DataContext } from "../../../context/DataContext";
import { isAccess, simpleMessage } from "../../../helpers/Helpers";
import { deleteToken, deleteUserData, getToken } from "../../../api/Account";
import NoData from "../../../components/NoData";
import ListFooter from "../../../components/ui/ListFooter";
import StatusChip from "../../../components/ui/StatusChip";

import SmallModal from "../../../components/modals/SmallModal";
import Swal from "sweetalert2";
import {
  deleteHolidayAsync,
  getHolidaysByYearAsync,
} from "../../../api/Holiday";
import AddHoliday from "./AddHoliday";
import moment from "moment/moment";
import { getTokens } from "../../../theme";

const HolidayList = () => {
  const [holidayList, setHolidayList] = useState([]);
  const [localReload, setLocalReload] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const { access, setIsLoading, setIsLogged, setIsUnautorized, isDarkMode } =
    useContext(DataContext);
  const t = getTokens(isDarkMode);

  const token = getToken();

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const result = await getHolidaysByYearAsync(token);
      if (!result.statusResponse) {
        setIsLoading(false);
        if (result.error === "Unauthorized") {
          setIsUnautorized(true);
          return;
        }
        simpleMessage(result.error, "error");
        return;
      }

      setIsLoading(false);
      setHolidayList(result.data.result);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localReload]);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsperPage] = useState(10);
  const indexLast = currentPage * itemsperPage;
  const indexFirst = indexLast - itemsperPage;
  const currentItem = holidayList.slice(indexFirst, indexLast);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const deleteHoliday = async (id) => {
    Swal.fire({
      title: "¿Estas Seguro?",
      text: "No podras revertir esto...!",
      icon: "warning",
      showCancelButton: true,

      confirmButtonColor: "#1d4ed8",
      cancelButtonColor: "#d83a3f",

      confirmButtonText: "Eliminar!",
      cancelButtonText: "Cancelar",
      customClass: {
        popup: "border",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        (async () => {
          setIsLoading(true);
          const result = await deleteHolidayAsync(token, id);
          if (!result.statusResponse) {
            setIsLoading(false);
            if (result.error === "eX01") {
              deleteUserData();
              deleteToken();
              setIsLogged(false);
              return;
            }
            if (result.error === "Unauthorized") {
              setIsUnautorized(true);
              return;
            }
            simpleMessage(result.error, "error");
            return;
          }
          setIsLoading(false);
          setLocalReload(!localReload);
          Swal.fire("Eliminado!", "Día festivo eliminado.", "success");
        })();
      }
    });
  };

  return (
    <div>
      <Paper sx={{ overflow: "hidden" }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="flex-end"
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={2}
          flexWrap="wrap"
          sx={{ padding: "18px 22px", borderBottom: "1px solid", borderColor: "divider" }}
        >
          {isAccess(access, "CONFIG CREATE") ? (
            <Button
              onClick={() => setShowModal(true)}
              startIcon={<AddRoundedIcon />}
              variant="contained"
              color="primary"
              sx={{ whiteSpace: "nowrap", height: "44px" }}
            >
              Agregar día festivo
            </Button>
          ) : null}
        </Stack>

        {isEmpty(currentItem) ? (
          <NoData />
        ) : (
          <>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: 720 }}>
                <TableHead>
                  <TableRow>
                    <TableCell align="center">#</TableCell>
                    <TableCell align="center">Fecha</TableCell>
                    <TableCell>Descripción</TableCell>
                    <TableCell>Sucursales</TableCell>
                    {isAccess(access, "CONFIG DELETE") ? (
                      <TableCell align="center">Eliminar</TableCell>
                    ) : null}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {currentItem.map((row) => {
                    const { description, festivo, id, sucursals } = row;

                    return (
                      <TableRow key={id} hover>
                        <TableCell align="center" sx={{ color: "text.disabled", fontWeight: 600 }}>
                          {id}
                        </TableCell>
                        <TableCell align="center" sx={{ color: "text.secondary" }}>
                          {moment(festivo).format("L")}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: "text.primary" }}>{description}</TableCell>
                        <TableCell>
                          <Stack spacing={1} direction="row" flexWrap="wrap" useFlexGap>
                            {sucursals.map((i) => (
                              <StatusChip key={i.id} label={i.description} tone="neutral" />
                            ))}
                          </Stack>
                        </TableCell>
                        {isAccess(access, "CONFIG DELETE") ? (
                          <TableCell align="center">
                            <Tooltip title="Eliminar">
                              <IconButton
                                size="small"
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: "9px",
                                  border: `1px solid ${t.fieldLine}`,
                                  backgroundColor: t.field,
                                  color: "error.main",
                                }}
                                onClick={() => deleteHoliday(id)}
                              >
                                <DeleteRoundedIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        ) : null}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <ListFooter
              data={holidayList}
              currentCount={currentItem.length}
              indexFirst={indexFirst}
              paginate={paginate}
              itemsperPage={itemsperPage}
              noun="días festivos"
            />
          </>
        )}
      </Paper>

      <SmallModal
        titulo={"Agregar Feriado"}
        isVisible={showModal}
        setVisible={setShowModal}
      >
        <AddHoliday
          setShowModal={setShowModal}
          localReload={localReload}
          setLocalReload={setLocalReload}
        />
      </SmallModal>
    </div>
  );
};

export default HolidayList;
